import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useTokens } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSightmapClickToActivate } from '../hooks/useSightmapClickToActivate';
import { contrastSafeTextColor } from '../utils/wcagContrast';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
  /** Heading shown above the blurb when the title token has no override */
  defaultTitle?: string;
}

/**
 * Headings for the pages that should introduce the blurb with an h2.
 * Keyed by the section tokenPrefix already set on those pages.
 */
const DEFAULT_TITLES: Record<string, string> = {
  'floor-plans-blurb': 'Find a Layout That Fits',
  'amenities-blurb': 'Amenities Worth Coming Home For',
  'neighborhood-blurb': 'Your Rock Creek Neighborhood',
  'virtual-tour-blurb': 'Walk Through From Home',
  'gallery-blurb': 'See the Spaces',
  'interactive-map-blurb': 'Find Your Place on the Map',
};

export function PageBlurb({ blurbField, tokenPrefix, defaultText, defaultTitle }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const tokens = useTokens();
  const linkColor = contrastSafeTextColor(tokens.color.primary, tokens.color.background);
  const prefix = tokenPrefix || 'pageblurb';
  const titlePath = `${prefix}.title`;
  const textPath = `${prefix}.text`;
  const titleDefault = defaultTitle || DEFAULT_TITLES[prefix] || '';
  const titleValue = useContentToken(titlePath, { defaultValue: titleDefault });
  const isInteractiveMap = prefix === 'interactive-map-blurb';

  // Portable click-to-activate for any Engrain Sightmap embed on this page.
  useSightmapClickToActivate(isInteractiveMap);

  return (
    <section ref={sectionRef} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {titleValue ? (
          <EditableToken
            path={titlePath}
            defaultValue={titleDefault}
            as="h2"
            className="mb-4 font-heading text-2xl font-normal text-[var(--vibe-color-text)] sm:text-3xl"
          >
            {titleValue}
          </EditableToken>
        ) : null}
        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="div"
          className="page-blurb-copy font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] space-y-4 [&_p]:mb-0"
          style={linkColor ? { ['--blurb-link-color' as string]: linkColor } : undefined}
        />
      </div>

      <style>{`
        .page-blurb-copy a {
          color: var(--blurb-link-color, var(--vibe-color-primary));
          text-decoration: underline;
        }
      `}</style>

      {/* Schema-lint anchors for static analysis — hidden at runtime */}
      <EditableToken path="pageblurb.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.title': {
      label: 'Body Title',
      type: 'text',
      defaultValue: '',
    },
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '',
    },
  },
};

(PageBlurb as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageBlurb;
